<template>
  <div ref="containerEl" class="ambassador-filter">
    <div class="filter-header">
      <label class="filter-label">Ambassador</label>
      <button v-if="modelValue !== null" class="filter-reset-btn" @click="handleReset">
        Clear
      </button>
    </div>
    <div class="filter-search">
      <input
        ref="inputEl"
        v-model="searchInput"
        type="text"
        placeholder="Cerca ambassador..."
        class="filter-input"
        @focus="isOpen = true"
      />
      <Teleport to="body">
        <div
          v-if="isOpen && searchInput && results.length > 0"
          class="filter-dropdown"
          :style="dropdownStyle"
        >
          <div
            v-for="ambassador in results"
            :key="ambassador.id"
            class="filter-item"
            @click="selectAmbassador(ambassador)"
          >
            <div class="filter-item-name">{{ ambassador.ambassador_name }}</div>
            <div class="filter-item-email">{{ ambassador.ambassador_email }}</div>
          </div>
        </div>
      </Teleport>
    </div>
    <div v-if="modelValue !== null" class="filter-selected">
      <div class="filter-selected-name">{{ selectedName }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { ambassadorService, type Ambassador } from '@/api/services/ambassador.service'

interface Props {
  modelValue: number | null
}

defineProps<Props>()
const emit = defineEmits<{ 'update:modelValue': [id: number | null] }>()

const searchInput = ref('')
const isOpen = ref(false)
const results = ref<Ambassador[]>([])
const selectedName = ref('')
const inputEl = ref<HTMLInputElement | null>(null)
const containerEl = ref<HTMLDivElement | null>(null)
const dropdownPos = ref({ top: 0, left: 0, width: 0 })
let debounceTimer: NodeJS.Timeout | null = null

const dropdownStyle = computed(() => ({
  top: `${dropdownPos.value.top}px`,
  left: `${dropdownPos.value.left}px`,
  width: `${dropdownPos.value.width}px`,
}))

const fetchAmbassadors = async () => {
  if (!searchInput.value.trim()) {
    results.value = []
    return
  }

  try {
    const response = await ambassadorService.search(searchInput.value, 20)
    results.value = response.data
  } catch (err) {
    console.error('Failed to fetch ambassadors:', err)
    results.value = []
  }
}

const debouncedSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    fetchAmbassadors()
  }, 300)
}

const selectAmbassador = (ambassador: Ambassador) => {
  searchInput.value = ''
  selectedName.value = ambassador.ambassador_name
  isOpen.value = false
  results.value = []
  emit('update:modelValue', ambassador.id)
}

const handleReset = () => {
  searchInput.value = ''
  selectedName.value = ''
  results.value = []
  isOpen.value = false
  emit('update:modelValue', null)
}

const handleClickOutside = (event: MouseEvent) => {
  if (containerEl.value && !containerEl.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

const updateDropdownPosition = () => {
  if (!inputEl.value) return
  const rect = inputEl.value.getBoundingClientRect()
  dropdownPos.value = {
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
  }
}

watch(searchInput, () => {
  if (searchInput.value) {
    debouncedSearch()
    updateDropdownPosition()
  } else {
    results.value = []
  }
})

watch(isOpen, () => {
  if (isOpen.value) {
    updateDropdownPosition()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
.ambassador-filter {
  padding: 1rem;
  border-bottom: 1px solid var(--cds-ui-03);
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.filter-label {
  font-weight: 600;
  color: var(--cds-text-01);
  margin: 0;
}

.filter-reset-btn {
  background: none;
  border: none;
  color: var(--cds-link-01);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  text-decoration: underline;
  transition: color 0.15s ease;

  &:hover {
    color: var(--cds-link-02);
  }
}

.filter-search {
  position: relative;
  margin-bottom: 0.5rem;
}

.filter-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--cds-ui-03);
  border-radius: 2px;
  font-size: 0.875rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--cds-interactive-01);
    box-shadow:
      0 2px 4px 0 rgba(0, 0, 0, 0.1),
      inset 0 0 0 2px var(--cds-interactive-01);
  }
}

.filter-dropdown {
  position: fixed;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid var(--cds-ui-03);
  z-index: 9999;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: 250px;
}

.filter-item {
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--cds-hover-light-ui);
  }
}

.filter-item-name {
  font-weight: 500;
  color: var(--cds-text-01);
  font-size: 0.875rem;
}

.filter-item-email {
  font-size: 0.75rem;
  color: var(--cds-text-02);
}

.filter-selected {
  padding: 0.5rem 0.75rem;
  background-color: var(--cds-ui-02);
  border-radius: 2px;
  font-size: 0.875rem;
}

.filter-selected-name {
  font-weight: 500;
  color: var(--cds-text-01);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
}
</style>
